import React, { useState } from "react";
import PropTypes from "prop-types";
import fallbackImage from "../../assets/img/img.png";

/**
 * A safe image component that falls back to a default image if the source fails to load.
 *
 * @param {Object} props - The component props.
 * @param {string} props.src - The source URL of the image.
 * @param {string} props.alt - The alt text for the image.
 * @param {string} [props.className] - Additional CSS classes.
 * @param {Object} [props.style] - Inline styles.
 * @param {string} [props.fallbackSrc] - Custom fallback image source.
 * @returns {JSX.Element} The rendered image component.
 */
const SafeImage = ({
  src,
  alt,
  className = "",
  style = {},
  fallbackSrc = fallbackImage,
  ...props
}) => {
  const [isError, setIsError] = useState(false);

  const handleError = (e) => {
    if (!isError) {
      setIsError(true);
    }
    if (props.onError) {
      props.onError(e);
    }
  };

  return (
    <img
      src={isError ? fallbackSrc : src || fallbackSrc}
      alt={alt}
      className={`${className} ${isError ? "fallback-img" : ""}`}
      style={style}
      onError={handleError}
      {...props}
    />
  );
};

SafeImage.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  fallbackSrc: PropTypes.string,
  onError: PropTypes.func,
};

export default SafeImage;
